<template>
  <div class="mantenedor-contratos p-4">
    <CardPrime>
      <template #title>
        <span class="text-xl font-semibold">Contratos</span>
      </template>
      <template #content>
        <p class="text-sm text-600 mb-4">
          Busca alumnos por colegio, curso y casa (Manchester, Bristol, etc.). Usa <strong>Ver</strong> para abrir el contrato en un modal.
          Selecciona alumnos (o usa todo el listado filtrado) para <strong>habilitar contratos</strong> de forma masiva.
        </p>

        <div class="flex flex-wrap gap-3 align-items-end mb-4">
          <div class="field">
            <label class="block text-sm mb-1">Colegio</label>
            <ColegioDropdown
              v-model="selectedColegio"
              :mostrarErrores="false"
              tooltip=""
              error=""
            />
          </div>
          <div class="field" style="min-width: 200px">
            <label class="block text-sm mb-1">Curso</label>
            <SingleCursoCombo
              :mostrarErrores="mostrarErroresFiltro"
              tooltip="Curso"
              :error="errorsFiltro.curso"
              :modelValue="selectedCurso"
              @update:modelValue="selectedCurso = $event"
            />
          </div>
          <div class="field" style="min-width: 180px">
            <label class="block text-sm mb-1">Casa (opcional)</label>
            <CursoCasaDropDown
              v-model="selectedCursoCasa"
              :mostrarErrores="false"
              tooltip="Filtrar por Manchester, Bristol, etc."
              error=""
            />
          </div>
          <div class="field" style="min-width: 220px">
            <label class="block text-sm mb-1">Buscar alumno</label>
            <InputText
              v-model="busquedaNombre"
              placeholder="Nombre, apellido o RUT"
              class="w-full"
              @keyup.enter="buscarAlumnos"
            />
          </div>
          <ButtonPrime label="Buscar alumnos" icon="pi pi-search" @click="buscarAlumnos" />
        </div>

        <div class="flex flex-wrap gap-2 mb-3">
          <ButtonPrime
            label="Habilitar contratos"
            icon="pi pi-check-circle"
            :disabled="!alumnos.length || habilitandoMasivo"
            :loading="habilitandoMasivo"
            @click="abrirModalHabilitar"
          />
          <ButtonPrime
            label="Promover curso"
            icon="pi pi-arrow-up"
            class="p-button-secondary"
            :disabled="!selectedAlumnos.length"
            @click="abrirModalPromo"
          />
        </div>

        <DataTable
          :value="alumnos"
          :loading="loading"
          dataKey="id_usuario"
          selectionMode="multiple"
          v-model:selection="selectedAlumnos"
          responsiveLayout="scroll"
          class="p-datatable-sm mb-4"
        >
          <Column selectionMode="multiple" headerStyle="width: 3rem" />
          <Column field="id_usuario" header="ID" style="width: 6rem" />
          <Column header="Nombre">
            <template #body="{ data }">
              {{ data.nombre }} {{ data.apellido_paterno }}
            </template>
          </Column>
          <Column field="identificador_rut" header="RUT" style="width: 9rem" />
          <Column field="email" header="Email" />
          <Column field="curso_nombre" header="Curso" />
          <Column field="curso_casa" header="Casa" style="width: 7rem" />
          <Column header="Contrato" style="width: 8rem">
            <template #body="{ data }">
              <ButtonPrime
                label="Ver"
                icon="pi pi-file"
                class="p-button-sm"
                @click="abrirPanelContrato(data)"
              />
            </template>
          </Column>
        </DataTable>
      </template>
    </CardPrime>

    <!-- Modal contrato (Ver) -->
    <DialogPrime
      v-model:visible="dialogContrato"
      :header="tituloModalContrato"
      :modal="true"
      :style="{ width: 'min(920px, 96vw)' }"
      :contentStyle="{ maxHeight: '78vh', overflow: 'auto' }"
      @hide="cerrarModalContrato"
    >
      <div v-if="usuarioContrato">
        <div v-if="!cabecera" class="mb-3">
          <p class="text-orange-700 m-0 mb-2">Este alumno no tiene fila en ESTUDYOO_CONTRATO.</p>
          <div class="flex flex-wrap gap-2 align-items-center">
            <label class="text-sm">Curso para SP (opcional):</label>
            <SingleCursoCombo
              :mostrarErrores="false"
              tooltip=""
              :error="''"
              :modelValue="cursoParaSp"
              @update:modelValue="cursoParaSp = $event"
            />
            <ButtonPrime
              label="Crear contrato (SP)"
              icon="pi pi-plus"
              @click="crearContratoSp"
            />
          </div>
        </div>

        <div v-else class="mb-4">
          <h4 class="text-md mt-0 mb-2">Cabecera (id_contrato {{ cabecera.id_contrato }})</h4>
          <div class="grid">
            <div class="col-12 md:col-4">
              <label class="block text-sm mb-1">Tipo contrato</label>
              <InputNumber v-model="formCabecera.id_tipo_contrato" class="w-full" :useGrouping="false" />
            </div>
            <div class="col-12 md:col-4">
              <label class="block text-sm mb-1">Tipo plan</label>
              <InputNumber v-model="formCabecera.id_tipo_plan" class="w-full" :useGrouping="false" />
            </div>
            <div class="col-12 md:col-4">
              <label class="block text-sm mb-1">Tipo pago</label>
              <InputNumber v-model="formCabecera.id_tipo_pago" class="w-full" :useGrouping="false" />
            </div>
            <div class="col-12 md:col-4">
              <label class="block text-sm mb-1">Inicio</label>
              <input v-model="formCabecera.fecha_inicio" type="date" class="w-full p-inputtext p-component" />
            </div>
            <div class="col-12 md:col-4">
              <label class="block text-sm mb-1">Término</label>
              <input v-model="formCabecera.fecha_termino" type="date" class="w-full p-inputtext p-component" />
            </div>
            <div class="col-12 md:col-4">
              <label class="block text-sm mb-1">Descuento vigente</label>
              <InputNumber v-model="formCabecera.descuento_vigente" class="w-full" :minFractionDigits="0" :maxFractionDigits="4" />
            </div>
            <div class="col-12 md:col-4 flex align-items-center gap-2 pt-4">
              <Checkbox v-model="formCabecera.vigente" :binary="true" inputId="vig" />
              <label for="vig">Vigente</label>
            </div>
            <div class="col-12 md:col-4 flex align-items-center gap-2 pt-4">
              <Checkbox v-model="formCabecera.bloqueado" :binary="true" inputId="blo" />
              <label for="blo">Bloqueado</label>
            </div>
          </div>
          <div class="flex flex-wrap gap-2 mt-3">
            <ButtonPrime
              label="Guardar cabecera (UPDATE)"
              icon="pi pi-save"
              @click="guardarCabecera"
            />
            <ButtonPrime
              label="Sincronizar con SP (crear/actualizar)"
              icon="pi pi-refresh"
              class="p-button-secondary"
              @click="sincronizarContratoSp"
            />
          </div>
        </div>

        <h4 class="text-md mt-4 mb-2">Detalle del contrato (ítems curso / asignatura)</h4>
        <DataTable :value="detalle" class="p-datatable-sm" responsiveLayout="scroll">
          <Column field="id_contrato_detalle" header="Id det." style="width: 5rem" />
          <Column field="curso" header="Curso" />
          <Column field="asignatura" header="Asignatura" />
          <Column field="valor_neto_item_contrato" header="Valor neto" />
          <Column field="porcentaje_IVA" header="IVA %" />
          <Column field="cantidad_licencias" header="Licencias" />
          <Column field="vigente" header="Vigente">
            <template #body="{ data }">
              {{ data.vigente ? 'Sí' : 'No' }}
            </template>
          </Column>
          <Column header="Acciones" style="width: 8rem">
            <template #body="{ data }">
              <ButtonPrime icon="pi pi-pencil" class="p-button-sm" @click="abrirEditarDetalle(data)" />
            </template>
          </Column>
        </DataTable>

        <div class="mt-4 p-3 border-round border-1 surface-border">
          <h5 class="mt-0">Agregar asignatura al contrato vigente</h5>
          <div class="flex flex-wrap gap-3 align-items-end">
            <div>
              <label class="block text-sm mb-1">Curso del ítem</label>
              <SingleCursoCombo
                :mostrarErrores="false"
                tooltip=""
                :error="''"
                :modelValue="cursoContratoAsig"
                @update:modelValue="cursoContratoAsig = $event"
              />
            </div>
            <div style="min-width: 220px">
              <label class="block text-sm mb-1">Asignaturas</label>
              <AsignaturaDropdown
                :mostrarErrores="false"
                tooltip=""
                :error="''"
                :modelValue="asignaturasAgregar"
                @update:modelValue="asignaturasAgregar = $event"
              />
            </div>
            <ButtonPrime label="Agregar" icon="pi pi-plus" @click="agregarAsignaturasContrato" />
          </div>
        </div>
      </div>
      <template #footer>
        <ButtonPrime label="Cerrar" class="p-button-secondary" @click="cerrarModalContrato" />
      </template>
    </DialogPrime>

    <DialogPrime v-model:visible="dialogDetalle" header="Editar línea de detalle" :modal="true" :style="{ width: '420px' }">
      <div v-if="filaDetalleEdit" class="field mb-2">
        <label class="block text-sm mb-1">Valor neto</label>
        <InputNumber v-model="filaDetalleEdit.valor_neto_item_contrato" class="w-full" />
      </div>
      <div v-if="filaDetalleEdit" class="field mb-2">
        <label class="block text-sm mb-1">IVA %</label>
        <InputNumber v-model="filaDetalleEdit.porcentaje_IVA" class="w-full" />
      </div>
      <div v-if="filaDetalleEdit" class="field mb-2">
        <label class="block text-sm mb-1">Licencias</label>
        <InputNumber v-model="filaDetalleEdit.cantidad_licencias" class="w-full" :useGrouping="false" />
      </div>
      <div v-if="filaDetalleEdit" class="field flex align-items-center gap-2 mb-3">
        <Checkbox v-model="filaDetalleEdit.vigente" :binary="true" inputId="vigd" />
        <label for="vigd">Vigente</label>
      </div>
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" @click="dialogDetalle = false" />
        <ButtonPrime label="Guardar" icon="pi pi-check" @click="guardarDetalleEdit" />
      </template>
    </DialogPrime>

    <DialogPrime
      v-model:visible="dialogHabilitar"
      header="Habilitar contratos (masivo)"
      :modal="true"
      :style="{ width: 'min(560px, 96vw)' }"
    >
      <p class="text-sm text-600 mb-3">
        Crea o sincroniza el contrato, marca la cabecera como <strong>vigente</strong> y agrega las asignaturas SIMCE seleccionadas,
        alumno por alumno. Si no hay selección en la tabla, se procesan todos los alumnos del listado actual
        (<strong>{{ cantidadObjetivosHabilitar }}</strong>).
      </p>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Curso del contrato (SP)</label>
        <SingleCursoCombo
          :mostrarErrores="mostrarErroresHabilitar"
          tooltip="Curso para crear/actualizar contrato"
          :error="errorsHabilitar.curso"
          :modelValue="cursoHabilitarMasivo"
          @update:modelValue="cursoHabilitarMasivo = $event"
        />
      </div>
      <div class="field mb-2">
        <label class="block text-sm mb-1">Asignaturas SIMCE</label>
        <AsignaturaDropdown
          :mostrarErrores="mostrarErroresHabilitar"
          tooltip="Asignaturas a agregar al detalle"
          :error="errorsHabilitar.asignaturas"
          :modelValue="asignaturasHabilitarMasivo"
          @update:modelValue="asignaturasHabilitarMasivo = $event"
        />
      </div>
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" :disabled="habilitandoMasivo" @click="dialogHabilitar = false" />
        <ButtonPrime
          label="Habilitar"
          icon="pi pi-check-circle"
          :loading="habilitandoMasivo"
          :disabled="habilitandoMasivo"
          @click="confirmarHabilitacionMasiva"
        />
      </template>
    </DialogPrime>

    <DialogPrime
      v-model:visible="dialogPromo"
      header="Promoción de curso"
      :modal="true"
      :style="{ width: 'min(480px, 96vw)' }"
    >
      <p class="text-sm text-600 mb-3">
        Se asignará el curso destino a <strong>{{ selectedAlumnos.length }}</strong> alumno(s) seleccionado(s).
      </p>
      <div class="field mb-2">
        <label class="block text-sm mb-1">Curso destino</label>
        <SingleCursoCombo
          :mostrarErrores="mostrarErroresPromo"
          tooltip="Nuevo curso"
          :error="errorsPromo.curso"
          :modelValue="cursoDestinoPromo"
          @update:modelValue="cursoDestinoPromo = $event"
        />
      </div>
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" @click="dialogPromo = false" />
        <ButtonPrime label="Promover" icon="pi pi-arrow-up" @click="confirmarPromocion" />
      </template>
    </DialogPrime>

    <DialogPrime
      v-model:visible="dialogResultadosHabilitar"
      header="Resultado habilitación de contratos"
      :modal="true"
      :style="{ width: 'min(720px, 96vw)' }"
      :contentStyle="{ maxHeight: '70vh', overflow: 'auto' }"
    >
      <div v-if="resumenHabilitacion" class="mb-3">
        <p class="m-0">
          <strong>{{ resumenHabilitacion.ok }}</strong> exitoso(s),
          <strong class="text-red-600">{{ resumenHabilitacion.error }}</strong> con error(es),
          de {{ resumenHabilitacion.total }} procesado(s).
        </p>
      </div>
      <DataTable
        v-if="resultadosHabilitacion.length"
        :value="resultadosHabilitacion"
        class="p-datatable-sm"
        responsiveLayout="scroll"
      >
        <Column header="Alumno">
          <template #body="{ data }">
            {{ data.nombre }} (id {{ data.id_usuario }})
          </template>
        </Column>
        <Column field="estado" header="Estado" style="width: 7rem">
          <template #body="{ data }">
            <span :class="data.ok ? 'text-green-700' : 'text-red-600'">{{ data.ok ? 'OK' : 'Error' }}</span>
          </template>
        </Column>
        <Column field="detalle" header="Detalle" />
      </DataTable>
      <template #footer>
        <ButtonPrime label="Cerrar" @click="dialogResultadosHabilitar = false" />
      </template>
    </DialogPrime>
  </div>
</template>

<script>
import CardPrime from 'primevue/card';
import ButtonPrime from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import DialogPrime from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Checkbox from 'primevue/checkbox';
import ColegioDropdown from '@/components/selectores/ColegioDropdown.vue';
import SingleCursoCombo from '@/components/selectores/SingleCursoCombo.vue';
import CursoCasaDropDown from '@/components/selectores/CursoCasaDropDown.vue';
import AsignaturaDropdown from '@/components/selectores/AsignaturaDropdown.vue';
import usuarioService from '@/services/UsuarioService';
import contratoService from '@/services/ContratoService';

const ID_USUARIO_ADMIN = 1;

function parseCursoId(val) {
  if (val === null || val === undefined || val === '') return null;
  const n = parseInt(String(val), 10);
  return Number.isNaN(n) ? null : n;
}

function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function msgError(e) {
  return e.response?.data?.ExceptionMessage
    || e.response?.data?.Message
    || e.message
    || 'Error desconocido';
}

function coincideCasa(casaAlumno, filtro) {
  if (!casaAlumno || !filtro) return false;
  const a = String(casaAlumno).trim().toUpperCase();
  const f = String(filtro).trim().toUpperCase();
  if (!a || !f) return false;
  if (a === f) return true;
  if (a.length === 1 && f.length > 1 && f.charAt(0) === a) return true;
  if (f.length === 1 && a.length > 1 && a.charAt(0) === f) return true;
  return false;
}

export default {
  name: 'MantenedorContratos',
  components: {
    CardPrime,
    ButtonPrime,
    DataTable,
    Column,
    DialogPrime,
    InputNumber,
    InputText,
    Checkbox,
    ColegioDropdown,
    SingleCursoCombo,
    CursoCasaDropDown,
    AsignaturaDropdown,
  },
  data() {
    return {
      selectedColegio: '',
      selectedCurso: '',
      selectedCursoCasa: '',
      busquedaNombre: '',
      mostrarErroresFiltro: false,
      errorsFiltro: { curso: '' },
      loading: false,
      alumnos: [],
      selectedAlumnos: [],
      usuarioContrato: null,
      dialogContrato: false,
      cabecera: null,
      detalle: [],
      formCabecera: {},
      cursoParaSp: '',
      cursoContratoAsig: '',
      asignaturasAgregar: [],
      cursoDestinoPromo: '',
      mostrarErroresPromo: false,
      errorsPromo: { curso: '' },
      dialogDetalle: false,
      filaDetalleEdit: null,
      dialogHabilitar: false,
      dialogPromo: false,
      cursoHabilitarMasivo: '',
      asignaturasHabilitarMasivo: [],
      mostrarErroresHabilitar: false,
      errorsHabilitar: { curso: '', asignaturas: '' },
      habilitandoMasivo: false,
      dialogResultadosHabilitar: false,
      resultadosHabilitacion: [],
      resumenHabilitacion: null,
    };
  },
  computed: {
    tituloModalContrato() {
      if (!this.usuarioContrato) return 'Contrato';
      return `Contrato: ${this.usuarioContrato.nombre} ${this.usuarioContrato.apellido_paterno} (id ${this.usuarioContrato.id_usuario})`;
    },
    cantidadObjetivosHabilitar() {
      return this.selectedAlumnos.length ? this.selectedAlumnos.length : this.alumnos.length;
    },
  },
  methods: {
    async buscarAlumnos() {
      const idCurso = parseCursoId(this.selectedCurso);
      if (!this.selectedColegio || idCurso == null || idCurso <= 0) {
        this.mostrarErroresFiltro = true;
        this.errorsFiltro.curso = 'Selecciona colegio y curso';
        return;
      }
      this.mostrarErroresFiltro = false;
      this.errorsFiltro.curso = '';
      this.loading = true;
      this.cerrarModalContrato();
      try {
        const response = await usuarioService.getAlumnosPorCurso({
          IdColegio: this.selectedColegio,
          IdCurso: idCurso,
          Page: 1,
          Rows: 500,
          q: this.busquedaNombre,
          cursoCasa: this.selectedCursoCasa,
        });
        const items = response.data?.items ?? response.data ?? [];
        let lista = Array.isArray(items) ? items : [];
        if (this.selectedCursoCasa && lista.some((a) => a && a.curso_casa)) {
          lista = lista.filter((a) => coincideCasa(a.curso_casa, this.selectedCursoCasa));
        }
        this.alumnos = lista;
        this.selectedAlumnos = [];
      } catch (e) {
        console.error(e);
        alert(msgError(e) || 'Error al listar alumnos');
      } finally {
        this.loading = false;
      }
    },
    async abrirPanelContrato(usuario) {
      this.usuarioContrato = usuario;
      this.dialogContrato = true;
      await this.refrescarContratoUsuario(usuario.id_usuario);
    },
    cerrarModalContrato() {
      this.dialogContrato = false;
      this.usuarioContrato = null;
      this.cabecera = null;
      this.detalle = [];
      this.formCabecera = {};
    },
    async refrescarContratoUsuario(idUsuario) {
      try {
        const [rCab, rDet] = await Promise.all([
          contratoService.obtenerCabecera(idUsuario),
          contratoService.obtenerDetalle(idUsuario),
        ]);
        this.cabecera = rCab.data || null;
        this.detalle = Array.isArray(rDet.data) ? rDet.data : [];
        this.formCabecera = this.cabecera
          ? {
              id_tipo_contrato: this.cabecera.id_tipo_contrato,
              id_tipo_plan: this.cabecera.id_tipo_plan,
              id_tipo_pago: this.cabecera.id_tipo_pago,
              fecha_inicio: toDateInput(this.cabecera.fecha_inicio_contrato),
              fecha_termino: toDateInput(this.cabecera.fecha_termino_contrato),
              descuento_vigente: this.cabecera.descuento_vigente,
              vigente: !!this.cabecera.vigente,
              bloqueado: !!this.cabecera.bloqueado,
            }
          : {};
      } catch (e) {
        console.error(e);
        alert(msgError(e) || 'Error al cargar contrato');
      }
    },
    parseIdCursoSp() {
      return parseCursoId(this.cursoParaSp);
    },
    async crearContratoSp() {
      if (!this.usuarioContrato) return;
      const idCurso = this.parseIdCursoSp();
      try {
        await contratoService.crearActualizarContrato({
          IdUsuarioAlum: this.usuarioContrato.id_usuario,
          IdCurso: idCurso,
          IdUsuarioAdmin: ID_USUARIO_ADMIN,
        });
        alert('Contrato creado o actualizado.');
        await this.refrescarContratoUsuario(this.usuarioContrato.id_usuario);
      } catch (e) {
        alert(msgError(e));
      }
    },
    async sincronizarContratoSp() {
      if (!this.usuarioContrato) return;
      const idCurso = parseCursoId(this.selectedCurso);
      try {
        await contratoService.crearActualizarContrato({
          IdUsuarioAlum: this.usuarioContrato.id_usuario,
          IdCurso: idCurso || null,
          IdUsuarioAdmin: ID_USUARIO_ADMIN,
        });
        alert('Contrato sincronizado con SP.');
        await this.refrescarContratoUsuario(this.usuarioContrato.id_usuario);
      } catch (e) {
        alert(msgError(e));
      }
    },
    async guardarCabecera() {
      if (!this.usuarioContrato || !this.cabecera) return;
      const p = {
        IdUsuario: this.usuarioContrato.id_usuario,
        IdTipoContrato: this.formCabecera.id_tipo_contrato,
        IdTipoPlan: this.formCabecera.id_tipo_plan,
        IdTipoPago: this.formCabecera.id_tipo_pago,
        FechaInicioContrato: this.formCabecera.fecha_inicio
          ? new Date(this.formCabecera.fecha_inicio + 'T12:00:00')
          : null,
        FechaTerminoContrato: this.formCabecera.fecha_termino
          ? new Date(this.formCabecera.fecha_termino + 'T12:00:00')
          : null,
        DescuentoVigente: this.formCabecera.descuento_vigente,
        Vigente: this.formCabecera.vigente,
        Bloqueado: this.formCabecera.bloqueado,
        IdUsuarioActualizacion: ID_USUARIO_ADMIN,
      };
      try {
        await contratoService.actualizarCabecera(p);
        alert('Cabecera guardada.');
        await this.refrescarContratoUsuario(this.usuarioContrato.id_usuario);
      } catch (e) {
        alert(msgError(e));
      }
    },
    abrirEditarDetalle(row) {
      this.filaDetalleEdit = {
        id_contrato_detalle: row.id_contrato_detalle,
        valor_neto_item_contrato: row.valor_neto_item_contrato,
        porcentaje_IVA: row.porcentaje_IVA,
        cantidad_licencias: row.cantidad_licencias,
        vigente: !!row.vigente,
      };
      this.dialogDetalle = true;
    },
    async guardarDetalleEdit() {
      if (!this.usuarioContrato || !this.filaDetalleEdit) return;
      try {
        await contratoService.actualizarDetalleItem({
          IdContratoDetalle: this.filaDetalleEdit.id_contrato_detalle,
          IdUsuario: this.usuarioContrato.id_usuario,
          ValorNetoItemContrato: this.filaDetalleEdit.valor_neto_item_contrato,
          PorcentajeIva: this.filaDetalleEdit.porcentaje_IVA,
          CantidadLicencias: this.filaDetalleEdit.cantidad_licencias,
          Vigente: this.filaDetalleEdit.vigente,
          IdUsuarioActualizacion: ID_USUARIO_ADMIN,
        });
        this.dialogDetalle = false;
        await this.refrescarContratoUsuario(this.usuarioContrato.id_usuario);
      } catch (e) {
        alert(msgError(e));
      }
    },
    payloadAsignatura(idUsuario, idCurso, idAsignatura) {
      return {
        IdUsuario: idUsuario,
        IdCurso: idCurso,
        IdAsignatura: idAsignatura,
        ValorNeto: 100000,
        PorcentajeIVA: 19,
        CantidadLicencias: 1,
        Vigente: true,
        IdUsuarioCreacion: ID_USUARIO_ADMIN,
        IdUsuarioActualizacion: ID_USUARIO_ADMIN,
      };
    },
    async agregarAsignaturasContrato() {
      if (!this.usuarioContrato) return;
      const idCurso = parseCursoId(this.cursoContratoAsig);
      const asigs = Array.isArray(this.asignaturasAgregar) ? this.asignaturasAgregar : [];
      if (!idCurso || !asigs.length) {
        alert('Selecciona curso del ítem y al menos una asignatura.');
        return;
      }
      try {
        for (const idAsig of asigs) {
          if (idAsig == null || idAsig === '') continue;
          await usuarioService.agregarAsignaturaAlContrato(
            this.payloadAsignatura(this.usuarioContrato.id_usuario, idCurso, Number(idAsig))
          );
        }
        alert('Asignaturas agregadas al contrato.');
        this.asignaturasAgregar = [];
        await this.refrescarContratoUsuario(this.usuarioContrato.id_usuario);
      } catch (e) {
        alert(msgError(e));
      }
    },
    abrirModalHabilitar() {
      if (!this.alumnos.length) {
        alert('Busca alumnos primero.');
        return;
      }
      this.mostrarErroresHabilitar = false;
      this.errorsHabilitar = { curso: '', asignaturas: '' };
      if (!this.cursoHabilitarMasivo && this.selectedCurso) {
        this.cursoHabilitarMasivo = this.selectedCurso;
      }
      this.dialogHabilitar = true;
    },
    abrirModalPromo() {
      if (!this.selectedAlumnos.length) {
        alert('Selecciona alumnos en la tabla.');
        return;
      }
      this.mostrarErroresPromo = false;
      this.errorsPromo = { curso: '' };
      this.dialogPromo = true;
    },
    confirmarHabilitacionMasiva() {
      const idCurso = parseCursoId(this.cursoHabilitarMasivo);
      const asigs = Array.isArray(this.asignaturasHabilitarMasivo) ? this.asignaturasHabilitarMasivo : [];
      this.errorsHabilitar.curso = !idCurso ? 'Selecciona el curso del contrato' : '';
      this.errorsHabilitar.asignaturas = !asigs.length ? 'Selecciona al menos una asignatura SIMCE' : '';
      this.mostrarErroresHabilitar = true;
      if (this.errorsHabilitar.curso || this.errorsHabilitar.asignaturas) return;

      const objetivos = this.selectedAlumnos.length ? this.selectedAlumnos : this.alumnos;
      if (!objetivos.length) {
        alert('No hay alumnos para habilitar. Busca alumnos primero.');
        return;
      }
      this.ejecutarHabilitacionMasiva();
    },
    async habilitarContratoAlumno(alumno, idCurso, idsAsignaturas) {
      const idUsuario = alumno.id_usuario;
      const pasos = [];

      await contratoService.crearActualizarContrato({
        IdUsuarioAlum: idUsuario,
        IdCurso: idCurso,
        IdUsuarioAdmin: ID_USUARIO_ADMIN,
      });
      pasos.push('contrato SP');

      const cab = await contratoService.obtenerCabecera(idUsuario);
      const cabData = cab.data || {};
      await contratoService.actualizarCabecera({
        IdUsuario: idUsuario,
        IdTipoContrato: cabData.id_tipo_contrato,
        IdTipoPlan: cabData.id_tipo_plan,
        IdTipoPago: cabData.id_tipo_pago,
        FechaInicioContrato: cabData.fecha_inicio_contrato || null,
        FechaTerminoContrato: cabData.fecha_termino_contrato || null,
        DescuentoVigente: cabData.descuento_vigente,
        Vigente: true,
        Bloqueado: !!cabData.bloqueado,
        IdUsuarioActualizacion: ID_USUARIO_ADMIN,
      });
      pasos.push('vigente');

      let asigOk = 0;
      for (const idAsig of idsAsignaturas) {
        try {
          await usuarioService.agregarAsignaturaAlContrato(
            this.payloadAsignatura(idUsuario, idCurso, idAsig)
          );
          asigOk += 1;
        } catch (e) {
          const errMsg = msgError(e);
          if (!/ya existe|duplicate|duplicad/i.test(errMsg)) {
            throw e;
          }
        }
      }
      pasos.push(`${asigOk}/${idsAsignaturas.length} asignaturas`);

      return pasos.join(', ');
    },
    async ejecutarHabilitacionMasiva() {
      const idCurso = parseCursoId(this.cursoHabilitarMasivo);
      const idsAsignaturas = (this.asignaturasHabilitarMasivo || [])
        .map((id) => parseInt(id, 10))
        .filter((n) => !Number.isNaN(n));
      const objetivos = this.selectedAlumnos.length ? [...this.selectedAlumnos] : [...this.alumnos];

      if (!idCurso || !idsAsignaturas.length || !objetivos.length) return;

      this.habilitandoMasivo = true;
      this.resultadosHabilitacion = [];
      this.resumenHabilitacion = null;

      const resultados = [];
      for (const alumno of objetivos) {
        const nombre = `${alumno.nombre || ''} ${alumno.apellido_paterno || ''}`.trim();
        try {
          const detalle = await this.habilitarContratoAlumno(alumno, idCurso, idsAsignaturas);
          resultados.push({
            id_usuario: alumno.id_usuario,
            nombre,
            ok: true,
            detalle,
          });
        } catch (e) {
          resultados.push({
            id_usuario: alumno.id_usuario,
            nombre,
            ok: false,
            detalle: msgError(e),
          });
        }
      }

      this.resultadosHabilitacion = resultados;
      this.resumenHabilitacion = {
        total: resultados.length,
        ok: resultados.filter((r) => r.ok).length,
        error: resultados.filter((r) => !r.ok).length,
      };
      this.habilitandoMasivo = false;
      this.dialogHabilitar = false;
      this.dialogResultadosHabilitar = true;
      await this.buscarAlumnos();
    },
    confirmarPromocion() {
      const idCurso = parseCursoId(this.cursoDestinoPromo);
      if (!idCurso) {
        this.mostrarErroresPromo = true;
        this.errorsPromo.curso = 'Selecciona curso destino';
        return;
      }
      if (!this.selectedAlumnos.length) {
        alert('Selecciona alumnos en la tabla.');
        return;
      }
      this.mostrarErroresPromo = false;
      this.errorsPromo.curso = '';
      this.ejecutarPromocion();
    },
    async ejecutarPromocion() {
      const idCurso = parseCursoId(this.cursoDestinoPromo);
      this.dialogPromo = false;
      if (!idCurso || !this.selectedAlumnos.length) return;
      try {
        await contratoService.promoverCurso({
          IdUsuarios: this.selectedAlumnos.map((a) => a.id_usuario),
          IdCurso: idCurso,
          IdUsuarioActualizador: ID_USUARIO_ADMIN,
        });
        alert('Cursos actualizados.');
        await this.buscarAlumnos();
      } catch (e) {
        alert(msgError(e));
      }
    },
  },
};
</script>

<style scoped>
.mantenedor-contratos :deep(.p-inputnumber-input) {
  width: 100%;
}
</style>
